import { useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext/ThemeProvider';
import accountSettingsService from '../../services/accountSettingsService';
import type { AccountPreferences } from '../../types';

export const AccountPreferencesSync = (): null => {
  const { user } = useAuth();
  const { language, setLanguage } = useLanguage();
  const {
    mode,
    preferences,
    setTheme,
    setFontSize,
    setAnimations,
    setReducedMotion,
    setHighContrast,
  } = useTheme();
  const account = user?.preferences;
  const hydratedUserId = useRef<string | null>(null);
  const lastSavedSignature = useRef('');
  const skipNextSave = useRef(false);
  const saveTimer = useRef<number | null>(null);

  const localPreferences = useMemo<AccountPreferences | null>(() => {
    if (!account) return null;
    return {
      ...account,
      language,
      appearance: {
        ...account.appearance,
        theme: mode,
        fontSize: preferences.fontSize,
        animations: preferences.animations,
        reducedMotion: preferences.reducedMotion,
        highContrast: preferences.highContrast,
      },
    };
  }, [
    account,
    language,
    mode,
    preferences.animations,
    preferences.fontSize,
    preferences.highContrast,
    preferences.reducedMotion,
  ]);

  useEffect(() => {
    if (!user || !account || hydratedUserId.current === user.id) return;
    if (language !== account.language) setLanguage(account.language);
    if (mode !== account.appearance.theme) setTheme(account.appearance.theme);
    if (preferences.fontSize !== account.appearance.fontSize) setFontSize(account.appearance.fontSize);
    if (preferences.animations !== account.appearance.animations) setAnimations(account.appearance.animations);
    if (preferences.reducedMotion !== account.appearance.reducedMotion) setReducedMotion(account.appearance.reducedMotion);
    if (preferences.highContrast !== account.appearance.highContrast) setHighContrast(account.appearance.highContrast);
    hydratedUserId.current = user.id;
    lastSavedSignature.current = JSON.stringify(account);
    skipNextSave.current = true;
  }, [account, language, mode, preferences.fontSize, preferences.animations, preferences.reducedMotion, preferences.highContrast, setLanguage, setTheme, setFontSize, setAnimations, setReducedMotion, setHighContrast, user]);

  useEffect(() => {
    if (!user || !localPreferences || hydratedUserId.current !== user.id) return;
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }
    const signature = JSON.stringify(localPreferences);
    if (signature === lastSavedSignature.current) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      accountSettingsService
        .savePreferences(user.id, localPreferences)
        .then(() => {
          lastSavedSignature.current = signature;
        })
        .catch(error => {
          lastSavedSignature.current = signature;
          console.warn('Failed to sync account preferences:', error);
        });
    }, 800);

    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [localPreferences, user]);

  return null;
};

export default AccountPreferencesSync;
